import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import { humanizeApi, progressApi } from '../services/api';

import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { TextArea } from './ui/TextArea';
import { StatusProgress } from './ui/StatusProgress';

const AIHumanizer = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [lastProcessedText, setLastProcessedText] = useState('');
  const [regenerationCount, setRegenerationCount] = useState(0);
  const [maxRegenerations] = useState(5);
  const { isAnonymous } = useAuth();
  const { credits, setCredits } = useCredits();
  const navigate = useNavigate();
  const eventSourceRef = useRef(null);
  
  // Processing steps
  const steps = ["Checking credits", "Enhancing Text", "Generating output"];
  
  // Track when input text changes to hide regenerate option if user modifies input
  useEffect(() => {
    if (inputText !== lastProcessedText && outputText) {
      // Reset regeneration state if user modifies input
      setRegenerationCount(0);
      setLastProcessedText('');
    }
  }, [inputText, lastProcessedText, outputText]);
  
  // Cleanup event source on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);
  
  // Calculate word count more accurately
  const wordCount = useMemo(() => {
    return inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
  }, [inputText]);
  
  // Handle progress updates from SSE
  const handleProgressUpdate = (data) => {
    // Map server step names to our step indexes
    const stepMap = {
      'credits': 0,
      'humanizing': 1,
      'generating': 2
    };
    
    const stepIndex = stepMap[data.step];
    
    if (stepIndex !== undefined) {
      setCurrentStep(stepIndex);
    }
  };
  
  // Handle completed humanization from SSE
  const handleComplete = (data) => {
    if (data.success) {
      // Ensure processing indicators are turned off
      setIsProcessing(false);
      
      // Set the final output and success messages
      setOutputText(data.data.humanized);
      
      // Update tracking for regenerate functionality
      setLastProcessedText(inputText);
      
      // Track regeneration count
      const currentRegenCount = data.data.isFreeRehumanization ? 
        (data.data.rehumanizationInfo?.count || 1) : 1;
      setRegenerationCount(currentRegenCount);
      
      // Create success message based on whether it was free or paid
      let successMessage;
      if (data.data.isFreeRehumanization) {
        successMessage = `Text regenerated successfully for FREE (${currentRegenCount}${getOrdinalSuffix(currentRegenCount)} regeneration). You have ${maxRegenerations - currentRegenCount} regenerations remaining for this text.`;
      } else {
        successMessage = `Text enhanced successfully! Used ${data.data.creditsUsed} credits. You have ${data.data.remainingCredits} credits remaining.`;
      }
      
      setSuccess(successMessage);
      setCredits(data.data.remainingCredits);
      
      // Clean up the event source
      if (eventSourceRef.current) {
        const currentEventSource = eventSourceRef.current;
        eventSourceRef.current = null; 
        setTimeout(() => {
          currentEventSource.close();
        }, 500);
      }
    } else {
      setError(data.message || 'Error enhancing text');
      setIsProcessing(false);
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
  };
  
  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };
  
  // Handle SSE errors
  const handleStreamError = (event) => {
    setError('Lost connection to the server. Please try again.');
    setIsProcessing(false);
    
    // Close the event source
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };
  
  // Fallback processing if SSE fails
  const fallbackProcessing = () => {
    // Simulate progress steps
    setCurrentStep(0);
    
    // Update steps with delays
    setTimeout(() => {
      setCurrentStep(1);
      
      setTimeout(() => {
        setCurrentStep(2);
        
        setTimeout(() => {
          setIsProcessing(false);
          
          // Ensure the event source is properly closed
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
        }, 2000);
      }, 3000);
    }, 1000);
  };
  
  const handleSubmit = async () => {
    if (!inputText.trim()) return;
    
    // If user is anonymous, redirect to login page
    if (isAnonymous) {
      navigate('/login');
      return;
    }
    
    // Reset notification states
    setError(null);
    setSuccess(null);
    
    // Check if the text has the minimum word count
    if (wordCount < 75) {
      setError('Please enter at least 75 words before enhancing.');
      return;
    }
    
    // Check if user has enough credits (1 credit per word)
    if (credits < wordCount) {
      setError('Not enough credits. You need 1 credit per word.');
      return;
    }
    
    // Initialize processing
    setCurrentStep(0);
    setIsProcessing(true);
    
    try {
      // Try to connect to progress stream before sending the humanize request
      eventSourceRef.current = await progressApi.connectToProgressStream(
        handleProgressUpdate,
        handleComplete,
        handleStreamError
      );
      
      // If we failed to create the event source, use fallback processing
      if (!eventSourceRef.current) {
        fallbackProcessing();
      }
      
      // Send the humanize request
      const response = await humanizeApi.humanizeContent(inputText, 'standard');
      
      // If we don't have SSE and the request succeeds immediately (unlikely)
      if (!eventSourceRef.current && response.success && response.data?.humanized) {
        setOutputText(response.data.humanized);
        
        setSuccess(`Text enhanced successfully! Used ${response.data.creditsUsed} credits. You have ${response.data.remainingCredits} credits remaining.`);
        setCredits(response.data.remainingCredits);
        setIsProcessing(false);
      }
    } catch (err) {
      setError(err.message || 'Error enhancing text');
      setIsProcessing(false);
      
      // Close the event source if there was an error
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
  };
  
  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setError(null);
    setSuccess(null);
    setLastProcessedText('');
    setRegenerationCount(0);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
  };

  // New function to handle regeneration
  const handleRegenerate = async () => {
    if (!inputText.trim() || regenerationCount >= maxRegenerations) return;
    
    // Reset notification states
    setError(null);
    setSuccess(null);
    
    // Initialize processing
    setCurrentStep(0);
    setIsProcessing(true);
    
    try {
      // Connect to progress stream
      eventSourceRef.current = await progressApi.connectToProgressStream(
        handleProgressUpdate,
        handleComplete,
        handleStreamError
      );
      
      if (!eventSourceRef.current) {
        fallbackProcessing();
      }
      
      // Send the humanize request (will be treated as free regeneration by backend)
      await humanizeApi.humanizeContent(inputText, 'standard');
      
    } catch (err) {
      setError(err.message || 'Error regenerating text');
      setIsProcessing(false);
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
  };

  // Simple logic to show regenerate button: if we have output and user hasn't changed input
  const canRegenerate = outputText && lastProcessedText && inputText === lastProcessedText && regenerationCount < maxRegenerations;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 select-none pointer-events-none">AI Writing Enhancer</h1>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {isAnonymous && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 px-4 py-3 rounded-lg select-none">
          <p>You're currently browsing as a guest. <strong>Sign up or log in</strong> to use the AI Writing Enhancer and get free credits!</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b dark:border-gray-700 flex items-center justify-between select-none">
              <h3 className="font-medium text-gray-700 dark:text-gray-300 pointer-events-none">Input</h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <span className={wordCount < 75 ? "text-red-500 dark:text-red-400 font-medium" : ""}>
                  {wordCount} words
                </span>
                {wordCount < 75 && <span className="text-red-500 dark:text-red-400"> (minimum 75)</span>}
              </div>
            </div>
            <TextArea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Enter text to enhance... (minimum 75 words)"
              isProcessing={isProcessing}
              className="min-h-[300px]"
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b dark:border-gray-700 flex items-center justify-between select-none">
              <h3 className="font-medium text-gray-700 dark:text-gray-300 pointer-events-none">Enhanced Output</h3>
              <div className="flex items-center gap-2">
                {/* Regenerate button */}
                {canRegenerate && !isProcessing && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRegenerate}
                    className="h-8 text-purple-600 border-purple-300 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-600 dark:hover:bg-purple-900/20 font-medium"
                  >
                    ↻ Regenerate ({maxRegenerations - regenerationCount} left)
                  </Button>
                )}
                
                {outputText && (
                  <Button variant="ghost" size="sm" onClick={handleCopy} className="h-8">
                    Copy
                  </Button>
                )}
              </div>
            </div>
            <div className="relative">
              <TextArea
                value={outputText}
                onChange={() => {}}
                placeholder="Enhanced text will appear here..."
                isProcessing={isProcessing}
                readOnly
                className="min-h-[300px]"
              />
              {isProcessing && (
                <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center">
                  <div className="w-64">
                    <StatusProgress steps={steps} currentStep={currentStep} />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          onClick={handleSubmit}
          disabled={!inputText.trim() || isProcessing || wordCount < 75}
          className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 text-white px-8 py-6 select-none"
          size="lg"
        >
          {isProcessing ? "Processing..." : isAnonymous ? "Login to Continue" : "Enhance Writing"}
        </Button>
        <Button
          variant="outline"
          onClick={handleClear}
          disabled={isProcessing || (!inputText && !outputText)}
          className="px-8 py-6 select-none"
          size="lg"
        >
          Clear All
        </Button>
      </div>

      {!isAnonymous && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 select-none">
          Available <span className="font-medium">{credits?.toLocaleString() || 0}</span> <span>Credits</span>
          {wordCount > 0 && !canRegenerate && (
            <span> • Will use <span className="font-medium">{wordCount}</span> credits for this text</span>
          )}
          {canRegenerate && (
            <span className="text-purple-600 dark:text-purple-400 font-medium"> • {maxRegenerations - regenerationCount} free regenerations remaining</span>
          )}
        </div>
      )}
    </div>
  );
};

export default AIHumanizer; 